'use client';

import Image from 'next/image'; // ALLOW-RAW-IMG: static prize-pool trophy art + podium medals, not user content
import { useTranslations } from 'next-intl';
import type * as React from 'react';
import { Icon } from '@/components/ui/icon';
import { InformationCircleIcon } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { SilkSheet } from '@/components/ui/silk-sheet';

// Frontend-only launch prize pool — a fixed pre-launch marketing figure. There is
// no backend: eligibility, raffle draw, and payouts are a post-launch concern and
// deliberately not modelled. This is static display copy.
const PRIZE_POOL = {
  totalPoolUsd: '10,000',
  splitTable: [
    { rankFrom: 1, rankTo: 1, shareUsd: '1,200' },
    { rankFrom: 2, rankTo: 2, shareUsd: '900' },
    { rankFrom: 3, rankTo: 3, shareUsd: '700' },
    { rankFrom: 4, rankTo: 4, shareUsd: '500' },
    { rankFrom: 5, rankTo: 5, shareUsd: '400' },
    { rankFrom: 6, rankTo: 10, shareUsd: '300' },
    { rankFrom: 11, rankTo: 20, shareUsd: '160' },
    { rankFrom: 21, rankTo: 35, shareUsd: '60' },
    { rankFrom: 36, rankTo: 50, shareUsd: '20' },
  ],
  raffle: { winners: 20, poolUsd: '2,000' },
} as const;

/**
 * `$10,000 prize pool` banner — the gold-trophy earn incentive. Rendered on the
 * rewards dashboard and on the early-access waitlist-success card (to lift the
 * "Start questing" click-through). Uses the STANDARD Panel surface with a
 * decorative gold "spotlight" overlay (the `morfi-prize-pool*` block in
 * globals.css). The ⓘ opens the prize breakdown modal.
 */
export function PrizeBanner({ onOpen }: { onOpen: () => void }): React.ReactNode {
  const tr = useTranslations('rewards');
  // ARBITRARY: fixed fiat USD marketing figure (not USDC; useFormattedCurrency is D6-only)
  const amount = `$${PRIZE_POOL.totalPoolUsd}`;
  return (
    <Panel data-testid="prize-banner" className="morfi-prize-pool relative overflow-hidden">
      {/* Decorative gold-glow / grain layers (behind content) — see globals.css. */}
      <div className="morfi-prize-pool__floor" aria-hidden />
      <div className="morfi-prize-pool__halo" aria-hidden />
      <div className="morfi-prize-pool__edge" aria-hidden />
      <div className="morfi-prize-pool__grain" aria-hidden />
      <PanelContent className="relative z-[3] flex items-center gap-3.5">
        <div className="morfi-prize-pool__ring">
          <div className="morfi-prize-pool__disc">
            <Image
              src="/images/rewards/prize-pool.png"
              alt=""
              width={40}
              height={40}
              className="size-10 object-contain"
              aria-hidden
            />
          </div>
        </div>
        <div className="flex min-w-0 flex-col gap-0.5 break-words text-left">
          <span className="flex items-center gap-1.5 text-body font-semibold text-foreground-primary">
            {tr('prizeBanner.title', { amount })}
            {/* ALLOW-RAW-BUTTON: bare info-icon affordance matching the dashboard's InfoTipModal triggers (AnimatedButton's icon mode renders a box) — opens the prize-breakdown modal */}
            <button
              type="button"
              data-testid="prize-open-btn"
              aria-haspopup="dialog"
              aria-label={tr('prizeBanner.learnMore')}
              onClick={onOpen}
              className="inline-flex shrink-0 cursor-pointer text-foreground-tertiary transition-colors hover:text-foreground-secondary"
            >
              <Icon icon={InformationCircleIcon} className="size-3" />
            </button>
          </span>
          <span className="text-caption text-foreground-secondary">
            {tr('prizeBanner.description')}
          </span>
        </div>
      </PanelContent>
    </Panel>
  );
}

export function PrizeModal({
  open,
  onOpenChange,
  onOpenTerms,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Closes this modal and opens the prize-pool Terms & Conditions modal. */
  onOpenTerms: () => void;
}): React.ReactNode {
  const tr = useTranslations('rewards');
  // ARBITRARY: fixed fiat USD marketing figure (not USDC; useFormattedCurrency is D6-only)
  const amount = `$${PRIZE_POOL.totalPoolUsd}`;
  return (
    <SilkSheet
      open={open}
      onOpenChange={onOpenChange}
      variant="detached"
      title={tr('prizeModal.title')}
      description={tr('prizeModal.subtitle', { amount })}
    >
      <div data-testid="prize-modal-content" className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between px-1 text-micro font-semibold uppercase tracking-wide text-foreground-tertiary">
            <span>{tr('prizeModal.positionHeader')}</span>
            <span>{tr('prizeModal.rewardHeader')}</span>
          </div>
          {PRIZE_POOL.splitTable.map((s) => {
            const single = s.rankFrom === s.rankTo;
            // The top three reuse the /social leaderboard podium medals.
            const medal = single && s.rankFrom <= 3 ? s.rankFrom : null;
            return (
              <div
                key={`${String(s.rankFrom)}-${String(s.rankTo)}`}
                className="flex items-center justify-between gap-2 rounded-panel-row border bg-background-tertiary px-4 py-3"
              >
                <span className="flex items-center gap-2 text-body text-foreground-secondary">
                  {medal !== null ? (
                    <Image
                      src={`/images/social/podium/rank-${String(medal)}.png`}
                      alt=""
                      width={24}
                      height={24}
                      className="size-6 shrink-0 object-contain"
                      aria-hidden
                    />
                  ) : null}
                  {single
                    ? tr('prizeModal.rankSingle', { rank: s.rankFrom })
                    : tr('prizeModal.rankRange', { from: s.rankFrom, to: s.rankTo })}
                </span>
                {/* ARBITRARY: fixed fiat USD marketing figure */}
                <span className="text-body font-semibold text-foreground-brand dark:text-white">
                  ${s.shareUsd}
                </span>
              </div>
            );
          })}
          <div className="flex items-center justify-between gap-2 rounded-panel-row border border-border-brand bg-background-brand px-4 py-3">
            <span className="text-body font-medium text-foreground-primary">
              {tr('prizeModal.raffle', { winners: PRIZE_POOL.raffle.winners })}
            </span>
            {/* ARBITRARY: fixed fiat USD marketing figure */}
            <span className="text-body font-semibold text-foreground-brand dark:text-white">
              ${PRIZE_POOL.raffle.poolUsd}
            </span>
          </div>
        </div>

        <p className="text-micro text-foreground-tertiary">
          {tr.rich('prizeModal.raffleNote', {
            terms: (chunks: React.ReactNode): React.ReactNode => (
              // ALLOW-RAW-BUTTON: inline text link inside a paragraph — AnimatedButton is block-sized and would break the sentence flow
              <button
                type="button"
                data-testid="prize-terms-link"
                onClick={onOpenTerms}
                className="underline underline-offset-2 transition-colors hover:text-foreground-primary"
              >
                {chunks}
              </button>
            ),
          })}
        </p>
      </div>
    </SilkSheet>
  );
}
